import { useEffect, useState } from "react";

import { css } from "@emotion/react";
import MuiTooltip from "@mui/material/Tooltip";

import { colors } from "@dotnet/telematics-design-system";

import ButtonDefault from "../ButtonDefault";
import { TOOLTIP_PLACEMENT } from "../Tooltips";
import Container from "./Container";
import { getFilledPercentage } from "./functions";
import Loader from "./Loader";
import { setBarItemStyle } from "./sharedStyles";
import { ProgressBarMultipleProps } from "./types";

const ProgressBarMultiple = ({
    id,
    titleLeft,
    titleRight,
    stats,
    titleColor,
    emptyStatsColor,
    emptyStatsHoverColor,
    tooltipContentForEmptyStats,
    tooltipProps,
    isLoading,
    onHoverOn,
    onHoverOff
}: ProgressBarMultipleProps) => {
    const { gray300 } = colors;

    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        if (!isLoading) {
            // Animate the width change after the data is set
            const timer = setTimeout(() => setIsMounted(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsMounted(false); // reset when loading starts again
        }
    }, [isLoading]);

    const nonEmptyStats = stats.filter(({ value }) => value > 0);
    const isTotalZero = nonEmptyStats.length === 0;

    const getFilledWidth = (value: number) => {
        const total = nonEmptyStats.reduce((acc, { value }) => acc + value, 0);

        return getFilledPercentage(total, value);
    };

    const getBorderRadius = (index: number) => {
        if (isLoading || nonEmptyStats.length === 1) {
            return "100px"; // Single item
        }

        if (index === 0) {
            return "100px 0 0 100px"; // First item
        }

        if (index === nonEmptyStats.length - 1) {
            return "0 100px 100px 0"; // Last item
        }

        return "0"; // Middle items
    };

    const renderContent = () => {
        if (isLoading) {
            return <Loader />;
        }

        if (isTotalZero) {
            return (
                <MuiTooltip
                    title={tooltipContentForEmptyStats}
                    placement={TOOLTIP_PLACEMENT.Top}
                    {...tooltipProps}
                >
                    <ButtonDefault
                        onMouseEnter={() => onHoverOn?.(id)}
                        onMouseLeave={() => onHoverOff?.(id)}
                        onTouchStart={() => onHoverOn?.(id)}
                        onTouchEnd={() => onHoverOff?.(id)}
                        css={css({
                            width: "100%",
                            cursor: tooltipContentForEmptyStats
                                ? "pointer"
                                : "auto",
                            padding: "4px 0"
                        })}
                    >
                        <span
                            css={setBarItemStyle(
                                emptyStatsColor || gray300,
                                "100px",
                                emptyStatsHoverColor
                            )}
                        />
                    </ButtonDefault>
                </MuiTooltip>
            );
        }

        return nonEmptyStats.map((item, i) => {
            const { id, value, color, hoverColor, tooltipContent } = item;

            const filledWidth = getFilledWidth(value);
            const borderRadius = getBorderRadius(i);

            return (
                <MuiTooltip
                    key={id}
                    title={tooltipContent}
                    placement={TOOLTIP_PLACEMENT.Top}
                    {...tooltipProps}
                >
                    <ButtonDefault
                        onMouseEnter={() => onHoverOn?.(id)}
                        onMouseLeave={() => onHoverOff?.(id)}
                        onTouchStart={() => onHoverOn?.(id)}
                        onTouchEnd={() => onHoverOff?.(id)}
                        css={css({
                            width: isMounted ? filledWidth : "0%",
                            cursor: tooltipContent ? "pointer" : "auto",
                            transition: "width 0.3s ease",
                            padding: "4px 0"
                        })}
                    >
                        <span
                            css={setBarItemStyle(
                                color,
                                borderRadius,
                                hoverColor
                            )}
                        />
                    </ButtonDefault>
                </MuiTooltip>
            );
        });
    };

    return (
        <Container
            titleLeft={titleLeft}
            titleRight={titleRight}
            titleColor={titleColor}
        >
            {renderContent()}
        </Container>
    );
};

export { ProgressBarMultiple };
